import React, { useMemo } from 'react'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'
import { format } from 'date-fns'
import { vi } from 'date-fns/locale'
import { examCategories } from '../constants/examCategories'
import { isHoliday, getHolidayName } from '../constants/holidays'
import { useIsMobile } from '../hooks/useIsMobile'

const MaxExamChart = ({ 
  getMaxForDay,
  getDaysToShow,
  getExamCount,
  monthFilter = { month: new Date().getMonth() + 1, year: new Date().getFullYear() }, 
  dateFilter = { startDate: '', endDate: '' } 
}) => {
  const isMobile = useIsMobile()

  // Process data to create chart data using the same logic as the table
  const chartData = useMemo(() => {
    const days = getDaysToShow()
    const today = new Date()
    
    const processedData = days.map(dayInfo => {
      const dateStr = format(dayInfo.date, 'yyyy-MM-dd')
      const isHolidayDate = isHoliday(dateStr)
      const holidayName = isHolidayDate ? getHolidayName(dateStr) : null
      
      // Set maxCount to 0 for holidays
      const maxCount = isHolidayDate ? 0 : getMaxForDay(dayInfo.date)
      const isToday = today.toDateString() === dayInfo.date.toDateString()
      
      // Tính toán chi tiết cho từng hạng mục
      const examDetails = {}
      
      // Skip processing medical data for holidays
      if (!isHolidayDate) {
        // Nhóm siêu âm - Fixed logic to handle multiple ultrasound categories properly
        const ultraSoundCategories = examCategories.filter(cat => cat.name.includes('Siêu âm') || cat.name.includes('SA'))
        let maxUltraSound = 0
        
        console.log(`Debug ${dayInfo.date.toDateString()}: Found ${ultraSoundCategories.length} ultrasound categories`)
        
        ultraSoundCategories.forEach((category) => {
          const categoryIndex = examCategories.findIndex(c => c.name === category.name)
          if (categoryIndex !== -1) {
            const morningCount = getExamCount(dayInfo.date, categoryIndex, 'morning')
            const afternoonCount = getExamCount(dayInfo.date, categoryIndex, 'afternoon')
            
            console.log(`Debug ${dayInfo.date.toDateString()} - ${category.name}:`, {
              categoryIndex,
              morningCount,
              afternoonCount,
              morning_isNaN: isNaN(morningCount),
              afternoon_isNaN: isNaN(afternoonCount)
            })
            
            // Debug logging for NaN values
            if (isNaN(morningCount) || isNaN(afternoonCount)) {
              console.warn('NaN detected in MaxExamChart ultrasound:', {
                date: dayInfo.date.toDateString(),
                category: category.name,
                morningCount,
                afternoonCount,
                categoryIndex
              })
            }
            
            const validMorning = isNaN(morningCount) ? 0 : morningCount
            const validAfternoon = isNaN(afternoonCount) ? 0 : afternoonCount
            maxUltraSound = Math.max(maxUltraSound, validMorning, validAfternoon)
          }
        })
        if (maxUltraSound > 0) examDetails['Siêu âm'] = maxUltraSound
      
        // X-quang
        const xrayIndex = examCategories.findIndex(cat => cat.name === 'X-quang')
        if (xrayIndex !== -1) {
          const xrayMorning = getExamCount(dayInfo.date, xrayIndex, 'morning')
          const xrayAfternoon = getExamCount(dayInfo.date, xrayIndex, 'afternoon')
          const maxXray = Math.max(xrayMorning, xrayAfternoon)
          if (maxXray > 0) examDetails['X-quang'] = maxXray
        }
        
        // Điện tâm đồ
        const ecgIndex = examCategories.findIndex(cat => cat.name === 'Điện tâm đồ')
        if (ecgIndex !== -1) {
          const ecgMorning = getExamCount(dayInfo.date, ecgIndex, 'morning')
          const ecgAfternoon = getExamCount(dayInfo.date, ecgIndex, 'afternoon')
          
          // Debug logging for NaN values
          if (isNaN(ecgMorning) || isNaN(ecgAfternoon)) {
            console.warn('NaN detected in MaxExamChart ECG:', {
              date: dayInfo.date.toDateString(),
              ecgMorning,
              ecgAfternoon,
              ecgIndex
            })
          }
          
          const validEcgMorning = isNaN(ecgMorning) ? 0 : ecgMorning
          const validEcgAfternoon = isNaN(ecgAfternoon) ? 0 : ecgAfternoon
          const maxEcg = Math.max(validEcgMorning, validEcgAfternoon)
          if (maxEcg > 0) examDetails['Điện tâm đồ'] = maxEcg
        }
        
        // Khám phụ khoa
        const gyneIndex = examCategories.findIndex(cat => cat.name === 'Khám phụ khoa')
        if (gyneIndex !== -1) {
          const gyneMorning = getExamCount(dayInfo.date, gyneIndex, 'morning')
          const gyneAfternoon = getExamCount(dayInfo.date, gyneIndex, 'afternoon')
          
          // Debug logging for NaN values
          if (isNaN(gyneMorning) || isNaN(gyneAfternoon)) {
            console.warn('NaN detected in MaxExamChart Gynecology:', {
              date: dayInfo.date.toDateString(),
              gyneMorning,
              gyneAfternoon,
              gyneIndex
            })
          }
          
          const validGyneMorning = isNaN(gyneMorning) ? 0 : gyneMorning
          const validGyneAfternoon = isNaN(gyneAfternoon) ? 0 : gyneAfternoon
          const maxGyne = Math.max(validGyneMorning, validGyneAfternoon)
          if (maxGyne > 0) examDetails['Khám phụ khoa'] = maxGyne
        }
        
        // Đo loãng xương
        const boneIndex = examCategories.findIndex(cat => cat.name === 'Đo loãng xương')
        if (boneIndex !== -1) {
          const boneMorning = getExamCount(dayInfo.date, boneIndex, 'morning')
          const boneAfternoon = getExamCount(dayInfo.date, boneIndex, 'afternoon')
          const maxBone = Math.max(boneMorning, boneAfternoon)
          if (maxBone > 0) examDetails['Đo loãng xương'] = maxBone
        }
      }
      
      return {
        date: format(dayInfo.date, 'yyyy-MM-dd'),
        displayDate: `${dayInfo.day}`,
        maxCount: maxCount,
        isToday: isToday,
        isHoliday: isHolidayDate,
        holidayName: holidayName,
        examDetails: examDetails
      }
    })
    
    // Debug: Log all processed data to see what's happening
    console.log('MaxExamChart processed data:', processedData.map(item => ({
      date: item.date,
      maxCount: item.maxCount,
      examDetails: item.examDetails
    })))
    
    return processedData // Show all days to debug the issue
    // return processedData.filter(item => item.maxCount > 0) // Only show days with data
  }, [getMaxForDay, getDaysToShow, getExamCount])

  // Calculate average
  const average = useMemo(() => {
    if (chartData.length === 0) return 0
    const total = chartData.reduce((sum, item) => sum + item.maxCount, 0)
    return Math.round(total / chartData.length)
  }, [chartData])

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-2">
            Ngày: {format(new Date(data.date), 'dd/MM/yyyy', { locale: vi })}
          </p>
          {data.isHoliday && (
            <p className="text-sky-600 font-medium mb-2">
              {data.holidayName}
            </p>
          )}
          <p className="text-blue-600 mb-2">
            Max cận lâm sàng: <span className="font-semibold">{data.maxCount || 0}</span>
          </p>
          {Object.entries(data.examDetails).map(([key, value]) => {
            // Ensure value is not NaN before displaying
            const displayValue = isNaN(value) ? 0 : value
            return (
              <p key={key} className="text-gray-600 text-sm">
                {key}: <span className="font-semibold">{displayValue} ca</span>
              </p>
            )
          })}
        </div>
      )
    }
    return null
  }

  if (chartData.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center h-64 text-gray-500">
          <p>Không có dữ liệu để hiển thị biểu đồ</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white p-6 mb-8">
      <div className="h-80 relative">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <XAxis 
              dataKey="displayDate" 
              tick={{ fontSize: 12 }}
              axisLine={true}
              tickLine={false}
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* Đường trung bình màu đỏ, nét đứt */}
            <ReferenceLine 
              y={average} 
              stroke="#ef4444" 
              strokeDasharray="5 5" 
              strokeWidth={1}
              label={{ 
                value: average, 
                position: "insideTopRight", 
                offset: 10,
                style: { 
                  textAnchor: 'end', 
                  fontSize: '12px', 
                  fill: '#ef4444',
                  fontWeight: 'bold'
                }
              }}
            />
            <Line 
              type="monotone" 
              dataKey="maxCount" 
              stroke="#000000" 
              strokeWidth={2}
              dot={(props) => {
                const { cx, cy, payload } = props
                const isToday = payload?.isToday
                
                // Find max value and determine which day should be highlighted
                const maxValue = Math.max(...chartData.map(d => d.maxCount))
                const maxDays = chartData.filter(d => d.maxCount === maxValue && maxValue > 0)
                
                let shouldHighlight = false
                if (maxDays.length > 0) {
                  if (maxDays.length === 1) {
                    // Only one max day, highlight it
                    shouldHighlight = payload?.maxCount === maxValue
                  } else {
                    // Multiple max days, choose the one closest to today (future preferred)
                    const today = new Date()
                    const currentDateStr = format(today, 'yyyy-MM-dd')
                    
                    // Separate future and past max days
                    const futureDays = maxDays.filter(d => d.date >= currentDateStr)
                    const pastDays = maxDays.filter(d => d.date < currentDateStr)
                    
                    let targetDay = null
                    if (futureDays.length > 0) {
                      // Choose closest future day
                      targetDay = futureDays.sort((a, b) => a.date.localeCompare(b.date))[0]
                    } else if (pastDays.length > 0) {
                      // Choose closest past day
                      targetDay = pastDays.sort((a, b) => b.date.localeCompare(a.date))[0]
                    }
                    
                    shouldHighlight = targetDay && payload?.date === targetDay.date
                  }
                }
                
                // Priority: Max value > Today > Normal
                if (shouldHighlight) {
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={isMobile ? 6 : 8}
                      fill="transparent"
                      stroke="#ef4444"
                      strokeWidth={3}
                    />
                  )
                } else if (isToday) {
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={isMobile ? 5 : 8}
                      fill="#ffffff"
                      stroke="#000000"
                      strokeWidth={2}
                    />
                  )
                } else {
                  if (isMobile) return null
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={4}
                      fill="#000000"
                      stroke="#000000"
                      strokeWidth={2}
                    />
                  )
                }
              }}
              activeDot={{ r: isMobile ? 5 : 8, stroke: '#000000', strokeWidth: 2, fill: '#ffffff' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default MaxExamChart
